import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, BedDouble, ShieldCheck, Copy } from "lucide-react";
import QRCode from "react-qr-code";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtINR, todayISO, addDaysISO, nightsBetween } from "@/lib/site";

const STEPS = ["Stay Details", "Select Room", "Guest Details", "Payment"];
// Official tariff is inclusive of all taxes (per Shree Somnath Trust pricing)
const TAX_RATE = 0;

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export default function Book() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [rooms, setRooms] = useState([]);
  const [search, setSearch] = useState({
    check_in: params.get("check_in") || addDaysISO(todayISO(), 2),
    check_out: params.get("check_out") || addDaysISO(todayISO(), 3),
    adults: Number(params.get("adults")) || 2,
    children: 0,
    rooms: Number(params.get("rooms")) || 1,
  });
  const [selections, setSelections] = useState(() => (params.get("room") ? { [params.get("room")]: 1 } : {}));
  const [allocation, setAllocation] = useState({});
  const [guest, setGuest] = useState({ name: "", phone: "", email: "", special_requests: "" });
  const [order, setOrder] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    document.title = "Book Your Stay | Sagar Darshan Guest House, Somnath";
    api.rooms().then(setRooms).catch(() => toast.error("Could not load rooms"));
  }, []);

  const nights = nightsBetween(search.check_in, search.check_out);
  const totalRooms = Object.values(selections).reduce((a, b) => a + b, 0);
  const selectedRooms = rooms.filter((r) => selections[r.id]);
  const estimate = useMemo(() => {
    if (!selectedRooms.length || nights < 1) return null;
    const charges = selectedRooms.reduce((sum, r) => sum + r.price * (selections[r.id] || 0) * nights, 0);
    const taxes = Math.round(charges * TAX_RATE);
    return { charges, taxes, total: charges + taxes };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selections, nights, rooms]);

  const setS = (k) => (e) => setSearch((s) => ({ ...s, [k]: e.target.value }));
  const setG = (k) => (e) => setGuest((g) => ({ ...g, [k]: e.target.value }));

  const next = () => {
    if (step === 0 && nights < 1) return toast.error("Check-out must be after check-in");
    if (step === 1 && totalRooms < 1) return toast.error("Please select at least one room");
    if (step === 1) {
      const allocated = selectedRooms.reduce((s, r) => s + (allocation[r.id] || 2) * selections[r.id], 0);
      const party = Number(search.adults) + Number(search.children);
      if (allocated < party) return toast.error(`Allocate space for all ${party} members across your selected rooms`);
    }
    if (step === 2 && (!guest.name || !guest.phone || !guest.email)) return toast.error("Please complete your details");
    setStep((s) => Math.min(3, s + 1));
    window.scrollTo(0, 0);
  };

  const pay = async () => {
    setPaying(true);
    try {
      const res = await api.initiateBooking({
        items: Object.entries(selections).map(([room_id, qty]) => ({ room_id, qty })),
        allocation: selectedRooms.map((r) => ({ room_id: r.id, members_per_room: allocation[r.id] || 2 })),
        check_in: search.check_in,
        check_out: search.check_out,
        adults: Number(search.adults),
        children: Number(search.children),
        name: guest.name,
        phone: guest.phone,
        email: guest.email,
        special_requests: guest.special_requests,
      });
      setOrder(res);

      if (res.payment_mode !== "razorpay") return; // show direct UPI payment panel

      const ok = await loadRazorpay();
      if (!ok) throw new Error("payment-script");
      const rzp = new window.Razorpay({
        key: res.razorpay_key_id,
        amount: res.total * 100,
        currency: "INR",
        name: "Sagar Darshan Guest House",
        description: `${selectedRooms.map((r) => r.name).join(", ")} · ${nights} night${nights > 1 ? "s" : ""}`,
        order_id: res.razorpay_order_id,
        prefill: { name: guest.name, email: guest.email, contact: guest.phone },
        theme: { color: "#0A1128" },
        method: { upi: true, card: true, netbanking: true, wallet: true },
        handler: async (response) => {
          try {
            await api.confirmBooking({
              booking_ref: res.booking_ref,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            });
            navigate(`/booking-confirmation/${res.booking_ref}`);
          } catch {
            toast.error("Payment verification failed. Please contact us with your payment ID.");
          }
        },
      });
      rzp.on("payment.failed", () => toast.error("Payment failed. Please try again."));
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not initiate payment. Please try again.");
    } finally {
      setPaying(false);
    }
  };

  const [utr, setUtr] = useState("");
  const [submittingUtr, setSubmittingUtr] = useState(false);
  const [copied, setCopied] = useState(false);

  const upiIntent = order
    ? `upi://pay?pa=${order.upi_id}&pn=${encodeURIComponent(order.payee_name || "Sagar Darshan Guest House")}&am=${order.total}&cu=INR&tn=${encodeURIComponent(order.booking_ref)}`
    : "";

  const copyUpi = async () => {
    try {
      await navigator.clipboard.writeText(order.upi_id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable */ }
  };

  const submitUtr = async () => {
    if (!/^[A-Za-z0-9]{6,22}$/.test(utr.trim())) {
      return toast.error("Enter a valid UTR / transaction reference (6-22 letters or digits)");
    }
    setSubmittingUtr(true);
    try {
      await api.submitUtr({ booking_ref: order.booking_ref, utr: utr.trim() });
      navigate(`/booking-confirmation/${order.booking_ref}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit reference. Please try again.");
    } finally {
      setSubmittingUtr(false);
    }
  };

  const field = "flex flex-col gap-1.5";
  const label = "text-[10px] font-semibold uppercase tracking-[0.25em] text-navy/50";

  return (
    <div data-testid="book-page" className="min-h-screen bg-sand pb-16 pt-32">
      <div className="mx-auto max-w-5xl px-5 lg:px-10">
        <span className="overline-label">Direct Booking</span>
        <h1 className="mt-3 font-display text-4xl tracking-tight text-navy sm:text-5xl">Book Your Stay</h1>

        {/* Stepper */}
        <div className="mt-10 grid grid-cols-4 gap-2" data-testid="booking-stepper">
          {STEPS.map((s, i) => (
            <div key={s} className="flex flex-col gap-2">
              <div className={`h-1 transition-colors duration-500 ${i <= step ? "bg-gold" : "bg-navy/10"}`} />
              <span className={`text-[10px] font-semibold uppercase tracking-[0.15em] sm:text-[11px] ${i <= step ? "text-navy" : "text-navy/40"}`}>
                {i + 1}. {s}
              </span>
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10"
          >
            {step === 0 && (
              <div data-testid="step-search" className="border border-navy/10 bg-white p-8 lg:p-10">
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  <div className={field}>
                    <label className={label} htmlFor="bk-checkin">Check-In</label>
                    <input id="bk-checkin" data-testid="book-checkin" type="date" className="input-lux" min={addDaysISO(todayISO(), 2)} value={search.check_in}
                      onChange={(e) => { setS("check_in")(e); if (e.target.value >= search.check_out) setSearch((s) => ({ ...s, check_out: addDaysISO(e.target.value, 1) })); }} />
                  </div>
                  <div className={field}>
                    <label className={label} htmlFor="bk-checkout">Check-Out</label>
                    <input id="bk-checkout" data-testid="book-checkout" type="date" className="input-lux" min={addDaysISO(search.check_in, 1)} value={search.check_out} onChange={setS("check_out")} />
                  </div>
                  <div className={field}>
                    <label className={label} htmlFor="bk-adults">Adults</label>
                    <select id="bk-adults" data-testid="book-adults" className="input-lux" value={search.adults} onChange={setS("adults")}>
                      {Array.from({ length: 50 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div className={field}>
                    <label className={label} htmlFor="bk-children">Children</label>
                    <select id="bk-children" data-testid="book-children" className="input-lux" value={search.children} onChange={setS("children")}>
                      {Array.from({ length: 26 }, (_, i) => i).map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div className="flex items-end">
                    <p className="text-sm text-navy/60">{nights} night{nights !== 1 ? "s" : ""} selected · room types on the next step</p>
                  </div>
                </div>
                <button data-testid="step1-next" onClick={next} className="btn-primary mt-9">Check Availability</button>
              </div>
            )}

            {step === 1 && (
              <div data-testid="step-rooms" className="grid gap-6">
                <p className="text-sm text-navy/60">Select one or more room types and set quantities - all in a single booking.</p>
                {rooms.map((r) => {
                  const qty = selections[r.id] || 0;
                  const lineTotal = r.price * qty * nights;
                  return (
                    <div
                      key={r.id}
                      data-testid={`select-room-${r.id}`}
                      className={`grid gap-0 border bg-white transition-[border-color,box-shadow] duration-300 sm:grid-cols-3 ${
                        qty > 0 ? "border-gold shadow-xl shadow-gold/10" : "border-navy/10"
                      }`}
                    >
                      <img src={r.image} alt={r.name} loading="lazy" className="aspect-[4/3] h-full w-full object-cover sm:aspect-auto" />
                      <div className="p-7 sm:col-span-2">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="font-display text-2xl text-navy">{r.name}</p>
                            <p className="mt-1 flex items-center gap-1 text-xs text-navy/60"><BedDouble size={12} /> {r.bed}</p>
                          </div>
                          <div className="flex items-center gap-2 border border-navy/15 px-2 py-1.5">
                            <button data-testid={`qty-minus-${r.id}`} aria-label="Reduce rooms"
                              onClick={() => setSelections((s) => { const n = { ...s }; if ((n[r.id] || 0) <= 1) delete n[r.id]; else n[r.id] -= 1; return n; })}
                              className="flex h-7 w-7 items-center justify-center text-lg text-navy transition-colors hover:text-gold-dark">−</button>
                            <span data-testid={`qty-${r.id}`} className="w-5 text-center text-sm font-semibold text-navy">{qty}</span>
                            <button data-testid={`qty-plus-${r.id}`} aria-label="Add rooms"
                              onClick={() => setSelections((s) => ({ ...s, [r.id]: Math.min(10, (s[r.id] || 0) + 1) }))}
                              className="flex h-7 w-7 items-center justify-center text-lg text-navy transition-colors hover:text-gold-dark">+</button>
                          </div>
                        </div>
                        <p className="mt-3 text-sm text-navy/60">{r.short}</p>
                        <p className="mt-4 font-display text-xl text-navy">
                          {fmtINR(r.price)} <span className="font-body text-xs text-navy/50">/ night · incl. taxes</span>
                          {qty > 0 && (
                            <span data-testid={`line-total-${r.id}`} className="ml-3 font-body text-sm font-medium text-gold-dark">
                              {qty} room{qty > 1 ? "s" : ""} · {fmtINR(lineTotal)} for {nights} night{nights !== 1 ? "s" : ""}
                            </span>
                          )}
                        </p>
                        {qty > 0 && (
                          <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-navy/10 pt-4">
                            <label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-navy/50" htmlFor={`members-${r.id}`}>
                              Members per room
                            </label>
                            <select
                              id={`members-${r.id}`}
                              data-testid={`members-${r.id}`}
                              value={allocation[r.id] || 2}
                              onChange={(e) => setAllocation((a) => ({ ...a, [r.id]: Number(e.target.value) }))}
                              className="border border-navy/20 bg-white px-3 py-1.5 text-sm text-navy outline-none transition-colors focus:border-gold"
                            >
                              {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => <option key={n} value={n}>{n}</option>)}
                            </select>
                            <span className="text-xs text-navy/50">
                              × {qty} room{qty > 1 ? "s" : ""} = <strong className="text-navy">{(allocation[r.id] || 2) * qty} members</strong>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                {totalRooms > 0 && estimate && (
                  <p data-testid="step2-total" className="border border-gold/40 bg-gold/10 px-5 py-3 text-sm text-navy">
                    Selected: <strong>{totalRooms} room{totalRooms > 1 ? "s" : ""}</strong> · Total <strong>{fmtINR(estimate.total)}</strong> for {nights} night{nights !== 1 ? "s" : ""} (incl. taxes)
                  </p>
                )}
                <div className="flex gap-4">
                  <button data-testid="step2-back" onClick={() => setStep(0)} className="btn-outline-dark">Back</button>
                  <button data-testid="step2-next" onClick={next} className="btn-primary">Continue</button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div data-testid="step-guest" className="border border-navy/10 bg-white p-8 lg:p-10">
                <div className="grid gap-6 sm:grid-cols-2">
                  <div className={field}>
                    <label className={label} htmlFor="bk-name">Full Name</label>
                    <input id="bk-name" data-testid="guest-name" className="input-lux" value={guest.name} onChange={setG("name")} />
                  </div>
                  <div className={field}>
                    <label className={label} htmlFor="bk-phone">Mobile Number</label>
                    <input id="bk-phone" data-testid="guest-phone" type="tel" className="input-lux" value={guest.phone} onChange={setG("phone")} />
                  </div>
                  <div className={field}>
                    <label className={label} htmlFor="bk-email">Email Address</label>
                    <input id="bk-email" data-testid="guest-email" type="email" className="input-lux" value={guest.email} onChange={setG("email")} />
                  </div>
                  <div className={field}>
                    <label className={label}>Guests</label>
                    <p className="input-lux bg-sand/50">{search.adults} Adult{search.adults > 1 ? "s" : ""}{search.children > 0 ? `, ${search.children} Child${search.children > 1 ? "ren" : ""}` : ""}</p>
                  </div>
                  <div className={`${field} sm:col-span-2`}>
                    <label className={label} htmlFor="bk-requests">Special Requests (optional)</label>
                    <textarea id="bk-requests" data-testid="guest-requests" rows={4} className="input-lux resize-none" value={guest.special_requests} onChange={setG("special_requests")} />
                  </div>
                </div>
                <div className="mt-9 flex gap-4">
                  <button data-testid="step3-back" onClick={() => setStep(1)} className="btn-outline-dark">Back</button>
                  <button data-testid="step3-next" onClick={next} className="btn-primary">Proceed to Payment</button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div data-testid="step-payment" className="grid gap-8 lg:grid-cols-5">
                <div className="border border-navy/10 bg-white p-8 lg:col-span-3 lg:p-10">
                  <p className="font-display text-2xl text-navy">Payment Summary</p>
                  <div className="mt-7 space-y-4 text-sm">
                    {selectedRooms.map((r) => (
                      <div key={r.id} className="flex justify-between text-navy/70">
                        <span>{r.name} × {selections[r.id]} × {nights} night{nights !== 1 ? "s" : ""}</span>
                        <span data-testid={`summary-line-${r.id}`}>{fmtINR(r.price * selections[r.id] * nights)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between text-navy/70">
                      <span>Taxes &amp; fees</span>
                      <span data-testid="summary-taxes">Included</span>
                    </div>
                    <div className="flex justify-between text-navy/70">
                      <span>Extra bed</span>
                      <span>Complimentary (on request)</span>
                    </div>
                    <div className="flex justify-between text-navy/70">
                      <span>Breakfast</span>
                      <span>Not included</span>
                    </div>
                    <div className="flex justify-between border-t border-navy/10 pt-4 font-display text-xl text-navy">
                      <span>Total Payable</span>
                      <span data-testid="summary-total">{estimate ? fmtINR(estimate.total) : "-"}</span>
                    </div>
                  </div>
                  <div className="mt-8 flex items-center gap-3 border border-gold/40 bg-gold/10 p-4 text-xs leading-relaxed text-navy/70">
                    <ShieldCheck size={18} className="shrink-0 text-gold-dark" />
                    100% advance payment compulsory · Free cancellation till check-in date with full refund to your source account · Secure UPI payment.
                  </div>
                  {!order ? (
                    <button data-testid="pay-now-btn" onClick={pay} disabled={paying}
                      className="btn-primary mt-8 w-full disabled:cursor-not-allowed disabled:opacity-60">
                      {paying ? "Preparing Payment..." : `Pay ${estimate ? fmtINR(estimate.total) : ""} Securely`}
                    </button>
                  ) : (
                    <div data-testid="upi-payment-panel" className="mt-8 border border-gold/40 bg-sand p-6">
                      <p className="text-center font-display text-lg text-navy">Pay with any UPI app</p>
                      <div className="mx-auto mt-5 w-fit border border-navy/10 bg-white p-3">
                        <QRCode data-testid="upi-qr-code" value={upiIntent} size={168} />
                      </div>
                      <div className="mt-5 flex items-center justify-center gap-3">
                        <span data-testid="upi-id-display" className="bg-navy px-4 py-2 text-sm font-medium tracking-wider text-gold">{order.upi_id}</span>
                        <button data-testid="upi-copy-btn" onClick={copyUpi}
                          className="flex items-center gap-1.5 border border-navy/20 px-3 py-2 text-xs uppercase tracking-[0.15em] text-navy transition-colors hover:border-navy">
                          {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy"}
                        </button>
                      </div>
                      <p className="mt-3 text-center text-xs text-navy/50">
                        Amount: <strong className="text-navy">{fmtINR(order.total)}</strong> · GPay, PhonePe, Paytm &amp; all UPI apps
                      </p>
                      <a data-testid="upi-app-pay-btn" href={upiIntent} className="btn-primary mt-5 w-full">
                        Open UPI App to Pay {fmtINR(order.total)}
                      </a>
                      <div className="my-6 flex items-center gap-3 text-[10px] uppercase tracking-[0.25em] text-navy/40">
                        <span className="h-px flex-1 bg-navy/10" /> After paying <span className="h-px flex-1 bg-navy/10" />
                      </div>
                      <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.25em] text-navy/50" htmlFor="bk-utr">
                        UTR / Transaction Reference Number
                      </label>
                      <input id="bk-utr" data-testid="utr-input" className="input-lux" placeholder="e.g. 523456789012"
                        value={utr} onChange={(e) => setUtr(e.target.value)} />
                      <button data-testid="utr-submit-btn" onClick={submitUtr} disabled={submittingUtr}
                        className="btn-primary mt-4 w-full disabled:cursor-not-allowed disabled:opacity-60">
                        {submittingUtr ? "Submitting..." : "Submit Payment Reference"}
                      </button>
                      <p className="mt-4 text-center text-[11px] leading-relaxed text-navy/40">
                        Your booking is confirmed once our team verifies your payment - usually within a few hours.
                      </p>
                    </div>
                  )}
                  <button data-testid="step4-back" onClick={() => setStep(2)} className="mt-4 text-xs uppercase tracking-[0.2em] text-navy/50 underline-offset-4 hover:underline">
                    Back to guest details
                  </button>
                </div>
                <div className="h-fit border border-navy/10 bg-navy p-8 text-sand lg:col-span-2">
                  <p className="overline-label !text-gold">Your Stay</p>
                  <p className="mt-4 font-display text-2xl">{selectedRooms.map((r) => r.name).join(" + ") || "Your selection"}</p>
                  <div className="mt-6 space-y-3 text-sm text-sand/70">
                    <p className="flex justify-between"><span>Check-in</span><span className="text-sand">{search.check_in}</span></p>
                    <p className="flex justify-between"><span>Check-out</span><span className="text-sand">{search.check_out}</span></p>
                    <p className="flex justify-between"><span>Guests</span><span className="text-sand">{search.adults}A{search.children > 0 ? ` · ${search.children}C` : ""}</span></p>
                    <p className="flex justify-between"><span>Rooms</span><span className="text-sand">{totalRooms}</span></p>
                    <p className="flex justify-between"><span>Guest</span><span className="text-sand">{guest.name}</span></p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
